import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { onboardingKey } from './onboardingWorkspace';
import { adminAPI, platformAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import {
  AdminEmptyState,
  AdminFilterCard,
  AdminPageHeader,
  AdminStatCard,
  AdminStatGrid,
  AdminTableCard,
} from '../../components/AdminSurface';
import { ComingSoonOverlay } from '../../components/ComingSoon';
import BillingRevenueTrendChart from '../../components/charts/BillingRevenueTrendChart';
import {
  ArrowTopRightOnSquareIcon,
  UsersIcon,
  CalendarIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

type DashboardReservation = {
  id: string;
  status: string;
  start_date: string;
  end_date: string;
  total_price: string | number;
  created_at: string;
  user?: {
    email?: string;
    first_name?: string;
    last_name?: string;
  };
  vehicle?: {
    make?: string;
    model?: string;
  };
};

function AdminDashboard() {
  const { user, operationsCompanyId } = useAuth();

  const isPlatformHost =
    typeof window !== 'undefined' &&
    window.location?.hostname?.toLowerCase().startsWith('globaladmin.');

  const isPlatformAdmin = isPlatformHost && Boolean((user as any)?.is_platform_admin);

  const { data: platformCompanies = [], isLoading: platformCompaniesLoading } = useQuery(
    'platform-dashboard-companies',
    () => platformAPI.getCompanies(),
    {
      enabled: isPlatformHost,
      select: (response) => {
        const data = response?.data;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.companies)) return data.companies;
        return [];
      },
      refetchInterval: 30000,
    }
  );

  const { data: platformUsers = [], isLoading: platformUsersLoading } = useQuery(
    'platform-dashboard-users',
    () => platformAPI.getUsers(),
    {
      enabled: isPlatformHost,
      select: (response) => {
        const data = response?.data;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.users)) return data.users;
        return [];
      },
      refetchInterval: 30000,
    }
  );

  const { data: supportStaff = [], isLoading: supportStaffLoading } = useQuery(
    'platform-dashboard-support-staff',
    () => platformAPI.getSupportStaff(),
    {
      enabled: isPlatformHost,
      select: (response) => {
        const data = response?.data;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.staff)) return data.staff;
        return [];
      },
      refetchInterval: 30000,
    }
  );

  const { data: platformInvoiceStats, isLoading: platformInvoiceStatsLoading } = useQuery(
    'platform-dashboard-invoice-stats',
    () => platformAPI.getInvoiceStats(),
    {
      enabled: isPlatformHost,
      select: (response) => response?.data,
      refetchInterval: 30000,
    }
  );

  const { data: platformBillingHealth, isLoading: platformBillingHealthLoading } = useQuery(
    'platform-dashboard-billing-health',
    () => platformAPI.getBillingHealth(),
    {
      enabled: isPlatformHost,
      select: (response) => response?.data,
      refetchInterval: 30000,
    }
  );

  const { data: platformBillingSummary, isLoading: platformBillingSummaryLoading } = useQuery(
    'platform-dashboard-billing-summary',
    () => platformAPI.getBillingSummary({ staleMinutes: 15, maxAttempts: 5 }),
    {
      enabled: isPlatformHost,
      select: (response) => response?.data,
      refetchInterval: 30000,
    }
  );

  const { data: platformBillingAlerts = [], isLoading: platformBillingAlertsLoading } = useQuery(
    'platform-dashboard-billing-alerts',
    () => platformAPI.getBillingAlerts({ limit: 8, page: 1, workflowStatus: 'open' }),
    {
      enabled: isPlatformHost,
      select: (response) => {
        const alerts = response?.data?.alerts;
        return Array.isArray(alerts) ? alerts : [];
      },
      refetchInterval: 30000,
    }
  );

  const { data: platformWebhookEvents = [], isLoading: platformWebhookEventsLoading } = useQuery(
    'platform-dashboard-webhook-events',
    () => platformAPI.getWebhookEvents({ limit: 20, page: 1 }),
    {
      enabled: isPlatformHost,
      select: (response) => {
        const events = response?.data?.events;
        return Array.isArray(events) ? events : [];
      },
      refetchInterval: 30000,
    }
  );

  const { data: platformAccessRequests = [], isLoading: platformAccessRequestsLoading } = useQuery(
    'platform-dashboard-access-requests',
    () => platformAPI.getAccessRequests(),
    {
      enabled: isPlatformHost,
      select: (response) => {
        const data = response?.data;
        return Array.isArray(data) ? data : [];
      },
      refetchInterval: 30000,
    }
  );

  // Setup is resumable and does not block dashboard access.
  const { data: onboardingData, isError: onboardingError, refetch: retryOnboarding } = useQuery(
    onboardingKey(operationsCompanyId || user?.company_id),
    () => adminAPI.getOnboardingStatus(),
    {
      enabled: !isPlatformAdmin && Boolean(operationsCompanyId || user?.company_id),
      select: (response) => response.data,
    }
  );

  // Check for pending access requests (company admins)
  const { data: accessRequests = [] } = useQuery(
    'admin-access-requests',
    () => adminAPI.getAccessRequests(),
    {
      enabled: !isPlatformAdmin,
      select: (response) => response.data,
    }
  );

  const { data: dashboardData, isLoading: dashboardLoading } = useQuery(
    'admin-dashboard',
    adminAPI.getDashboard,
    {
      enabled: !isPlatformAdmin,
      select: (response) => response.data,
      refetchInterval: 30000 // Refresh every 30 seconds
    }
  );

  const { data: realtimeData } = useQuery(
    'admin-realtime',
    adminAPI.getRealtimeMetrics,
    {
      enabled: !isPlatformAdmin,
      select: (response) => response.data,
      refetchInterval: 5000 // Refresh every 5 seconds
    }
  );

  const overview = dashboardData?.overview;
  const alerts = dashboardData?.alerts || [];
  const recentReservations: DashboardReservation[] = dashboardData?.recentReservations || [];
  const upcomingCheckouts: DashboardReservation[] = dashboardData?.upcomingCheckouts || [];
  const realtime = realtimeData;

  const platformLoading =
    platformCompaniesLoading ||
    platformUsersLoading ||
    supportStaffLoading ||
    platformInvoiceStatsLoading ||
    platformBillingHealthLoading ||
    platformBillingSummaryLoading ||
    platformBillingAlertsLoading ||
    platformWebhookEventsLoading ||
    platformAccessRequestsLoading;

  const billingReadyCompanies = platformCompanies.filter((company: any) => company?.stripe_charges_enabled).length;
  const activeCompanies = platformCompanies.filter((company: any) => company?.status === 'active').length;
  const suspendedCompanies = platformCompanies.filter((company: any) => company?.status === 'suspended').length;
  const platformAdminCount = platformUsers.filter((u: any) => u?.role === 'admin').length;
  const failedWebhookEvents = platformWebhookEvents.filter((event: any) => event?.status === 'failed' || event?.status === 'dead_letter');
  const failedBillingAlerts = platformBillingAlerts.filter((alert: any) => alert?.delivery_status === 'failed');

  const formatPlatformLabel = (value?: string) => {
    if (!value) return 'Unknown';
    return value.replaceAll('_', ' ');
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'warning': return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'info': return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
      default: return <ClockIcon className="h-5 w-5 text-on-surface-variant" />;
    }
  };

  const formatCurrency = (amount: string | number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(typeof amount === 'string' ? parseFloat(amount) : amount);
  };

  const formatCurrencyFromCents = (amount: number | undefined) => {
    return formatCurrency((amount || 0) / 100);
  };

  const formatDate = (value?: string | Date | null, options?: Intl.DateTimeFormatOptions) => {
    if (!value) return 'N/A';

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';

    return new Intl.DateTimeFormat('en-US', options || {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const reservationStatusTone = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-success-container text-success-on-container';
      case 'confirmed':
        return 'bg-info-container text-info-on-container';
      case 'pending':
        return 'bg-warning-container text-warning-on-container';
      case 'cancelled':
        return 'bg-error-container text-error-on-container';
      case 'completed':
        return 'bg-surface-container-high text-on-surface';
      default:
        return 'bg-surface-container-low text-on-surface-variant';
    }
  };

  const healthChecks = [
    { label: 'API', ok: Boolean(realtime?.systemHealth?.api) },
    { label: 'Database', ok: Boolean(realtime?.systemHealth?.database) },
    { label: 'Email', ok: Boolean(realtime?.systemHealth?.email) },
    { label: 'Payments', ok: Boolean(realtime?.systemHealth?.payments) },
  ];

  const operatorActions = [
    {
      to: '/admin/customers',
      title: 'Customers',
      description: 'See growth, recent signups, and account verification progress.',
      icon: <UsersIcon className="h-6 w-6" />,
      tone: 'text-primary',
    },
    {
      to: '/admin/billing',
      title: 'Billing Ops',
      description: 'Open disputes, fee activity, and payment exceptions in one place.',
      icon: <ChartBarIcon className="h-6 w-6" />,
      tone: 'text-primary',
    },
    {
      to: '/admin/settings',
      title: 'Settings',
      description: 'Complete setup, review access requests, and tune tenant controls.',
      icon: <ShieldCheckIcon className="h-6 w-6" />,
      tone: 'text-on-surface-variant',
    },
    {
      to: '/admin/invoices',
      title: 'Invoices',
      description: 'Chase overdue payments and finalize customer billing.',
      icon: <CurrencyDollarIcon className="h-6 w-6" />,
      tone: 'text-success',
    },
    {
      to: '/admin/analytics',
      title: 'Analytics',
      description: 'Track revenue, billing trends, and operator health.',
      icon: <ChartBarIcon className="h-6 w-6" />,
      tone: 'text-primary',
    },
    {
      to: '/admin/reservations',
      title: 'Reservations',
      description: 'Booking operations are still in rollout and remain preview-only here.',
      icon: <CalendarIcon className="h-6 w-6" />,
      tone: 'text-info',
      comingSoon: true,
      comingSoonLabel: 'Reservations module coming soon',
    },
    {
      to: '/admin/vehicles',
      title: 'Fleet',
      description: 'Fleet controls are still in rollout and shown below as a preview.',
      icon: <TruckIcon className="h-6 w-6" />,
      tone: 'text-warning',
      comingSoon: true,
      comingSoonLabel: 'Fleet module coming soon',
    },
  ];

  const revenueTrendData = useMemo(
    () => (dashboardData?.revenueChart || []).map((point: { date: string; amount: number }) => ({
      label: formatDate(point.date, { month: 'short', day: 'numeric' }),
      amount: Number(point.amount || 0),
    })),
    [dashboardData?.revenueChart]
  );

  const utilizationTrendData = useMemo(
    () => (dashboardData?.utilizationChart || []).map((point: { date: string; utilization: number }) => ({
      label: formatDate(point.date, { month: 'short', day: 'numeric' }),
      utilization: Number(point.utilization || 0),
    })),
    [dashboardData?.utilizationChart]
  );

  const totalPendingActions =
    (realtime?.pendingActions?.expiredReservations || 0) +
    (realtime?.pendingActions?.failedPayments || 0) +
    (realtime?.pendingActions?.customerSupport || 0);

  const tenantHealthLabel = healthChecks.every((check) => check.ok) ? 'healthy' : 'attention needed';

  const reservationGuestLabel = (reservation: DashboardReservation) => {
    const firstName = reservation.user?.first_name?.trim();
    const lastName = reservation.user?.last_name?.trim();
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    return fullName || reservation.user?.email || 'Unknown guest';
  };

  const vehicleLabel = (reservation: DashboardReservation) => {
    const make = reservation.vehicle?.make?.trim();
    const model = reservation.vehicle?.model?.trim();
    const label = [make, model].filter(Boolean).join(' ').trim();

    return label || 'Unassigned vehicle';
  };

  if (!isPlatformAdmin && dashboardLoading) {
    return (
      <PageLayout>
        <LoadingSpinner className="py-20" />
      </PageLayout>
    );
  }

  if (isPlatformAdmin) {
    if (platformLoading) {
      return (
        <PageLayout>
          <LoadingSpinner className="py-20" />
        </PageLayout>
      );
    }

    return (
      <PageLayout className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-on-surface">Global Admin</h1>
          <p className="mt-2 text-on-surface-variant">
            Run tenant lifecycle, billing reliability, webhook recovery, and platform operations from one view.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="card p-4 flex items-center gap-3">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
            <div>
              <p className="text-sm text-on-surface-variant">Tenants</p>
              <p className="text-xl font-bold text-on-surface">{platformCompanies.length}</p>
              <p className="text-xs text-on-surface-variant">{activeCompanies} active · {suspendedCompanies} suspended</p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-3">
            <ShieldCheckIcon className="h-8 w-8 text-violet-600" />
            <div>
              <p className="text-sm text-on-surface-variant">Platform Operators</p>
              <p className="text-xl font-bold text-on-surface">{platformAdminCount + supportStaff.length}</p>
              <p className="text-xs text-on-surface-variant">{platformAdminCount} admins · {supportStaff.length} support staff</p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-3">
            <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            <div>
              <p className="text-sm text-on-surface-variant">Platform Fee Revenue</p>
              <p className="text-xl font-bold text-on-surface">
                {formatCurrencyFromCents(platformInvoiceStats?.total_platform_revenue)}
              </p>
              <p className="text-xs text-on-surface-variant">
                {platformInvoiceStats?.total_invoices || 0} invoices · {billingReadyCompanies} billing-ready tenants
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-3">
            <LinkIcon className="h-8 w-8 text-amber-600" />
            <div>
              <p className="text-sm text-on-surface-variant">Webhook Reliability</p>
              <p className="text-xl font-bold text-on-surface">{failedWebhookEvents.length}</p>
              <p className="text-xs text-on-surface-variant">
                {platformBillingSummary?.webhook_events?.deadLetter || 0} dead-letter · {platformBillingSummary?.webhook_events?.retryableFailed || 0} retryable
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Link to="/platform/companies" className="card p-5 hover:shadow-elevated transition-shadow">
            <h3 className="text-base font-semibold text-on-surface">Tenant Lifecycle</h3>
            <p className="mt-2 text-sm text-on-surface-variant">Create, suspend, activate, and review billing readiness across all companies.</p>
          </Link>
          <Link to="/platform/users" className="card p-5 hover:shadow-elevated transition-shadow">
            <h3 className="text-base font-semibold text-on-surface">Identity & Access</h3>
            <p className="mt-2 text-sm text-on-surface-variant">Manage platform users, support staff permissions, and account controls.</p>
          </Link>
          <Link to="/platform/billing" className="card p-5 hover:shadow-elevated transition-shadow">
            <h3 className="text-base font-semibold text-on-surface">Billing Operations</h3>
            <p className="mt-2 text-sm text-on-surface-variant">Monitor alerts, replay webhooks, and run reconciliation/recovery actions.</p>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <div className="p-6 pb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-on-surface">Billing Alert Inbox</h3>
              <Link to="/platform/billing" className="text-sm text-primary hover:underline">Open Queue</Link>
            </div>
            <div className="px-6 pb-6 space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-pill bg-surface-container-low text-on-surface-variant">Open {platformBillingAlerts.length}</span>
                <span className="px-2 py-1 rounded-pill bg-error-container text-error-on-container">Failed Delivery {failedBillingAlerts.length}</span>
                <span className="px-2 py-1 rounded-pill bg-warning-container text-warning-on-container">Needs Review {platformBillingSummary?.alerts?.needsAttention || 0}</span>
              </div>
              {platformBillingAlerts.length === 0 ? (
                <p className="text-sm text-on-surface-variant">No open billing alerts right now.</p>
              ) : (
                platformBillingAlerts.slice(0, 5).map((alert: any) => (
                  <div key={alert.id} className="rounded-card border border-outline p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-on-surface">{alert.subject || alert.alert_type}</p>
                      <span className="text-xs text-on-surface-variant uppercase">{formatPlatformLabel(alert.severity)}</span>
                    </div>
                    <p className="mt-1 text-xs text-on-surface-variant line-clamp-2 [line-clamp:2]">{alert.message}</p>
                    <p className="mt-1 text-[11px] text-on-surface-variant">
                      {formatPlatformLabel(alert.target)} · {formatPlatformLabel(alert.delivery_status)} · {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="card">
            <div className="p-6 pb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-on-surface">Webhook & Access Operations</h3>
              <Link to="/platform/billing" className="text-sm text-primary hover:underline">Open Recovery</Link>
            </div>
            <div className="px-6 pb-6 space-y-4">
              <div className="rounded-card border border-outline p-3">
                <p className="text-sm font-medium text-on-surface">Billing Health</p>
                <p className="mt-1 text-sm text-on-surface-variant">
                  Status: <span className="font-semibold text-on-surface">{formatPlatformLabel(platformBillingHealth?.status)}</span>
                </p>
                <p className="text-xs text-on-surface-variant mt-1">
                  Invoice Sync dead-letter: {platformBillingSummary?.invoice_sync_jobs?.deadLetter || 0} · retryable: {platformBillingSummary?.invoice_sync_jobs?.retryableFailed || 0}
                </p>
              </div>

              <div className="rounded-card border border-outline p-3">
                <p className="text-sm font-medium text-on-surface">Failed Webhook Events</p>
                {failedWebhookEvents.length === 0 ? (
                  <p className="mt-1 text-sm text-on-surface-variant">No failed or dead-letter webhook events in current window.</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {failedWebhookEvents.slice(0, 4).map((event: any) => (
                      <div key={event.id} className="text-xs text-on-surface-variant">
                        <span className="font-medium text-on-surface">{event.event_type}</span>
                        <span> · {formatPlatformLabel(event.status)} · attempts {event.attempts || 0}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-card border border-outline p-3">
                <p className="text-sm font-medium text-on-surface">Pending Access Requests</p>
                {platformAccessRequests.filter((request: any) => request.status === 'pending').length === 0 ? (
                  <p className="mt-1 text-sm text-on-surface-variant">No pending platform access requests.</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {platformAccessRequests
                      .filter((request: any) => request.status === 'pending')
                      .slice(0, 4)
                      .map((request: any) => (
                        <div key={request.id} className="text-xs text-on-surface-variant">
                          <span className="font-medium text-on-surface">{request.company_name || request.company_id}</span>
                          <span> · requested {request.requested_at ? new Date(request.requested_at).toLocaleDateString() : 'recently'}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout className="space-y-8">
      {/* Setup Banner */}
      {onboardingError && <p role="alert">Setup readiness is unavailable. <button className="underline" onClick={() => void retryOnboarding()}>Retry</button></p>}
      {onboardingData && (!onboardingData.workspace_ready || !onboardingData.payment_ready || !onboardingData.onboarding_completed) && (
        <div className="mb-6 p-4 bg-warning-container rounded-card flex flex-wrap gap-3 items-center justify-between">
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
            <p className="text-sm text-warning-on-container">
              <span className="font-medium">Workspace {onboardingData.workspace_ready ? 'ready' : 'needs business basics'}.</span>{' '}
              Payments {onboardingData.payment_ready ? 'ready' : 'not ready'}.
            </p>
          </div>
          <Link
            to="/admin/onboarding"
            className="text-sm font-medium text-warning-on-container hover:underline underline"
          >
            Open Setup
          </Link>
        </div>
      )}

      {/* Access Request Banner */}
      {accessRequests.length > 0 && (
        <div className="mb-6 p-4 bg-info-container rounded-card flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <UsersIcon className="h-5 w-5 text-blue-600" />
            <p className="text-sm text-info-on-container">
              <span className="font-medium">{accessRequests.length} admin(s)</span> have requested access to your instance.
            </p>
          </div>
          <Link
            to="/admin/settings"
            className="text-sm font-medium text-info-on-container hover:underline underline"
          >
            Review in Settings
          </Link>
        </div>
      )}
      <AdminPageHeader
        eyebrow="Tenant operations"
        title="Operator Dashboard"
        description="Focus the tenant team on billing, invoices, customers, and settings while reservations and fleet continue rolling out."
        actions={(
          <>
            <Link to="/admin/billing" className="btn-secondary inline-flex items-center gap-2">
              <CurrencyDollarIcon className="h-4 w-4" />
              <span>Billing</span>
            </Link>
            <Link to="/admin/invoices" className="btn-secondary inline-flex items-center gap-2">
              <DocumentTextIcon className="h-4 w-4" />
              <span>Invoices</span>
            </Link>
            <Link to="/admin/analytics" className="btn-primary inline-flex items-center gap-2">
              <ChartBarIcon className="h-4 w-4" />
              <span>Analytics</span>
            </Link>
          </>
        )}
      />

      <BillingRevenueTrendChart
        className="mb-8"
        source="tenant"
        showFeeComparison={false}
        days={14}
      />

      {overview ? (
        <div className="mb-8">
          <AdminStatGrid>
            <AdminStatCard
              icon={<CurrencyDollarIcon className="h-7 w-7" />}
              label="Revenue This Month"
              value={formatCurrency(overview.revenue.thisMonth)}
              helper={`${formatCurrency(overview.revenue.today)} today · ${formatCurrency(overview.revenue.thisWeek)} last 7 days`}
              tone="success"
            />
            <AdminStatCard
              icon={<UsersIcon className="h-7 w-7" />}
              label="Customer Base"
              value={overview.customers.total}
              helper={`${overview.customers.activeThisMonth} active this month · ${overview.customers.newThisWeek} new this week`}
              tone="primary"
            />
            {/* <AdminStatCard
              icon={<ExclamationTriangleIcon className="h-7 w-7" />}
              label="Open Operator Work"
              value={totalPendingActions}
              helper={`${realtime?.pendingActions?.failedPayments || 0} overdue payments · ${realtime?.alertsCount || 0} alerts live`}
              tone="warning"
            /> */}
            {/* <ComingSoonOverlay label="Reservations coming soon">
              <AdminStatCard
                icon={<CalendarIcon className="h-7 w-7" />}
                label="Reservations In Flight"
                value={overview.reservations.active + overview.reservations.confirmed}
                helper={`${overview.reservations.pending} pending · ${overview.reservations.completed} completed`}
                tone="info"
              />
            </ComingSoonOverlay>
            <ComingSoonOverlay label="Fleet coming soon">
              <AdminStatCard
                icon={<TruckIcon className="h-7 w-7" />}
                label="Fleet Ready"
                value={`${overview.vehicles.available}/${overview.vehicles.total}`}
                helper={`${overview.vehicles.utilization}% utilization · ${overview.vehicles.maintenance} in maintenance`}
                tone="warning"
              />
            </ComingSoonOverlay> */}
          </AdminStatGrid>
        </div>
      ) : null}

      {/* <AdminFilterCard
        className="mb-8"
        title="Current Focus"
        description="Tenant operators should use billing, invoices, customers, and settings first. Reservations and fleet are visible below as rollout previews only."
      >
        <div className="flex flex-wrap gap-2">
          <span className="admin-soft-pill inline-flex rounded-pill bg-success-container px-3 py-1.5 text-xs font-semibold text-success-on-container">Primary: Billing</span>
          <span className="admin-soft-pill inline-flex rounded-pill bg-info-container px-3 py-1.5 text-xs font-semibold text-info-on-container">Primary: Customers</span>
          <span className="admin-soft-pill inline-flex rounded-pill bg-surface-container-high px-3 py-1.5 text-xs font-semibold text-on-surface">Preview: Reservations</span>
          <span className="admin-soft-pill inline-flex rounded-pill bg-surface-container-high px-3 py-1.5 text-xs font-semibold text-on-surface">Preview: Fleet</span>
        </div>
      </AdminFilterCard> */}

      {/* {realtime ? (
        <AdminFilterCard
          className="mb-8"
          title="Operator Pulse"
          description="Live operational signals refreshed every few seconds so the team can spot work before it turns into backlog."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="admin-subpanel rounded-card p-4">
              <p className="text-sm text-on-surface-variant">Active Reservations</p>
              <p className="mt-1 text-2xl font-bold text-on-surface">{realtime.activeReservations}</p>
              <p className="mt-1 text-xs text-on-surface-variant">Confirmed + active bookings in progress.</p>
            </div>
            <div className="admin-subpanel rounded-card p-4">
              <p className="text-sm text-on-surface-variant">Payments Last Hour</p>
              <p className="mt-1 text-2xl font-bold text-on-surface">{realtime.paymentsLastHour}</p>
              <p className="mt-1 text-xs text-on-surface-variant">Recently captured invoice payments.</p>
            </div>
            <div className="admin-subpanel rounded-card p-4">
              <p className="text-sm text-on-surface-variant">Open Alerts</p>
              <p className="mt-1 text-2xl font-bold text-on-surface">{realtime.alertsCount}</p>
              <p className="mt-1 text-xs text-on-surface-variant">{totalPendingActions} pending action items across billing and reservations.</p>
            </div>
            <div className="admin-subpanel rounded-card p-4">
              <p className="text-sm text-on-surface-variant">System Health</p>
              <p className="mt-1 text-2xl font-bold capitalize text-on-surface">{tenantHealthLabel}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {healthChecks.map((check) => (
                  <span
                    key={check.label}
                    className={`admin-soft-pill inline-flex rounded-pill px-2 py-1 text-[11px] font-semibold ${check.ok ? 'bg-success-container text-success-on-container' : 'bg-error-container text-error-on-container'}`}
                  >
                    {check.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </AdminFilterCard>
      ) : null} */}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {operatorActions.map((action) => (
          action.comingSoon ? (
            <ComingSoonOverlay key={action.to} label={action.comingSoonLabel}>
              <div className="admin-subpanel rounded-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className={`mb-3 ${action.tone}`}>{action.icon}</div>
                    <h3 className="text-base font-semibold text-on-surface">{action.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-on-surface-variant">{action.description}</p>
                  </div>
                  <ArrowTopRightOnSquareIcon className="mt-1 h-4 w-4 text-on-surface-variant" />
                </div>
              </div>
            </ComingSoonOverlay>
          ) : (
            <Link key={action.to} to={action.to} className="admin-subpanel rounded-card p-5 transition-transform hover:-translate-y-0.5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className={`mb-3 ${action.tone}`}>{action.icon}</div>
                  <h3 className="text-base font-semibold text-on-surface">{action.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-on-surface-variant">{action.description}</p>
                </div>
                <ArrowTopRightOnSquareIcon className="mt-1 h-4 w-4 text-on-surface-variant" />
              </div>
            </Link>
          )
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* <AdminTableCard
          title="Revenue Trend"
          description="Paid invoice revenue over the last 30 days."
          meta={overview ? `Month to date: ${formatCurrency(overview.revenue.thisMonth)}` : undefined}
        >
          <div className="h-80 px-4 pb-6 md:px-6">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueTrendData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tickFormatter={(value) => `$${Number(value).toFixed(0)}`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value: number | string) => formatCurrency(Number(value || 0))} />
                <Line type="monotone" dataKey="amount" stroke="#0f766e" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </AdminTableCard> */}

        {/* <AdminTableCard
          title="Customer Momentum"
          description="Keep acquisition and billing activity moving while booking and fleet modules are still rolling out."
          meta={overview ? `${overview.customers.activeThisMonth} active this month` : undefined}
        >
          <div className="grid grid-cols-1 gap-4 px-4 pb-6 md:grid-cols-2 md:px-6">
            <div className="admin-subpanel rounded-card p-4">
              <p className="text-sm text-on-surface-variant">Active This Month</p>
              <p className="mt-1 text-2xl font-bold text-on-surface">{overview?.customers.activeThisMonth || 0}</p>
              <p className="mt-1 text-xs text-on-surface-variant">Customers with invoice activity in the current month.</p>
            </div>
            <div className="admin-subpanel rounded-card p-4">
              <p className="text-sm text-on-surface-variant">New This Week</p>
              <p className="mt-1 text-2xl font-bold text-on-surface">{overview?.customers.newThisWeek || 0}</p>
              <p className="mt-1 text-xs text-on-surface-variant">Fresh customer records entering the tenant.</p>
            </div>
            <div className="admin-subpanel rounded-card p-4">
              <p className="text-sm text-on-surface-variant">Paid Today</p>
              <p className="mt-1 text-2xl font-bold text-on-surface">{formatCurrency(overview?.revenue.today || 0)}</p>
              <p className="mt-1 text-xs text-on-surface-variant">Immediate cash movement to review with invoices.</p>
            </div>
            <div className="admin-subpanel rounded-card p-4">
              <p className="text-sm text-on-surface-variant">Payments Last Hour</p>
              <p className="mt-1 text-2xl font-bold text-on-surface">{realtime?.paymentsLastHour || 0}</p>
              <p className="mt-1 text-xs text-on-surface-variant">Useful for confirming invoice collection activity.</p>
            </div>
          </div>
        </AdminTableCard> */}
      </div>

      {/* <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="space-y-6">
          <AdminTableCard
            title="Attention Queue"
            description="Live issues that need operator follow-up."
            meta={<Link to="/admin/billing" className="text-primary hover:underline">Open billing</Link>}
          >
            {alerts.length > 0 ? (
              <div className="space-y-3 px-4 pb-6 md:px-6">
                {alerts.slice(0, 5).map((alert: any) => (
                  <div key={alert.id} className="admin-subpanel rounded-card p-4">
                    <div className="flex items-start gap-3">
                      {getAlertIcon(alert.type)}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-on-surface">{alert.title}</p>
                          <span className={`admin-soft-pill inline-flex rounded-pill px-2 py-1 text-[11px] font-semibold ${alert.actionRequired ? 'bg-warning-container text-warning-on-container' : 'bg-surface-container-low text-on-surface-variant'}`}>
                            {alert.actionRequired ? 'Action required' : 'Monitor'}
                          </span>
                        </div>
                        <p className="mt-1 text-sm leading-6 text-on-surface-variant">{alert.message}</p>
                        <p className="mt-1 text-xs text-on-surface-variant">{formatDate(alert.timestamp)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <AdminEmptyState
                icon={<CheckCircleIcon className="h-6 w-6" />}
                title="No urgent issues"
                description="Billing and tenant controls are currently within normal bounds."
              />
            )}
          </AdminTableCard>

          <AdminTableCard title="System Health" description="Service-level checks behind the operator console.">
            <div className="grid grid-cols-1 gap-3 px-4 pb-6 md:grid-cols-2 md:px-6">
              {healthChecks.map((check) => (
                <div key={check.label} className="admin-subpanel flex items-center justify-between rounded-card p-4">
                  <div>
                    <p className="text-sm font-medium text-on-surface">{check.label}</p>
                    <p className="text-xs text-on-surface-variant">{check.ok ? 'Operational' : 'Needs review'}</p>
                  </div>
                  <span className={`h-2.5 w-2.5 rounded-full ${check.ok ? 'bg-success' : 'bg-error'}`} />
                </div>
              ))}
            </div>
          </AdminTableCard>
        </div>

        <AdminTableCard
          title="Reservations & Fleet Preview"
          description="These surfaces are still rolling out. Treat the data below as preview context, not the primary operator workflow yet."
          meta={<span className="text-warning">Coming soon</span>}
        >
          <div className="space-y-6 px-4 pb-6 md:px-6">
            <ComingSoonOverlay label="Fleet dashboard module coming soon">
              <div className="admin-subpanel rounded-card p-4">
                <h3 className="text-base font-semibold text-on-surface">Fleet Utilization</h3>
                <p className="mt-1 text-sm text-on-surface-variant">Daily vehicle utilization based on confirmed, active, and completed reservations.</p>
                <div className="mt-4 h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={utilizationTrendData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={24} />
                      <YAxis tickFormatter={(value) => `${Number(value).toFixed(0)}%`} tick={{ fontSize: 11 }} domain={[0, 100]} />
                      <Tooltip formatter={(value: number | string) => `${Number(value || 0).toFixed(0)}%`} />
                      <Line type="monotone" dataKey="utilization" stroke="#2563eb" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </ComingSoonOverlay>

            <ComingSoonOverlay label="Reservations module coming soon">
              <div className="admin-subpanel rounded-card p-4">
                <h3 className="text-base font-semibold text-on-surface">Upcoming Checkouts</h3>
                <p className="mt-1 text-sm text-on-surface-variant">Active reservations ending today or tomorrow.</p>
                {upcomingCheckouts.length === 0 ? (
                  <div className="pt-4">
                    <AdminEmptyState
                      icon={<ClockIcon className="h-6 w-6" />}
                      title="No near-term checkouts"
                      description="There are no active reservations ending in the immediate window."
                    />
                  </div>
                ) : (
                  <div className="mt-4 overflow-x-auto">
                    <table className="admin-table-grid min-w-full">
                      <thead className="admin-table-head">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Guest</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Vehicle</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Checkout</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-on-surface-variant">Value</th>
                        </tr>
                      </thead>
                      <tbody className="admin-table-body">
                        {upcomingCheckouts.slice(0, 4).map((reservation) => (
                          <tr key={reservation.id} className="admin-table-row">
                            <td className="px-4 py-3 text-sm text-on-surface">
                              <div className="font-medium">{reservationGuestLabel(reservation)}</div>
                              <div className="text-xs text-on-surface-variant">{reservation.user?.email || 'No email on file'}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-on-surface">{vehicleLabel(reservation)}</td>
                            <td className="px-4 py-3 text-sm text-on-surface">{formatDate(reservation.end_date)}</td>
                            <td className="px-4 py-3 text-sm font-medium text-on-surface">{formatCurrency(reservation.total_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </ComingSoonOverlay>

            <ComingSoonOverlay label="Reservations workflow coming soon">
              <div className="admin-subpanel rounded-card p-4">
                <h3 className="text-base font-semibold text-on-surface">Recent Reservations</h3>
                <p className="mt-1 text-sm text-on-surface-variant">Newest bookings across the tenant.</p>
                {recentReservations.length === 0 ? (
                  <div className="pt-4">
                    <AdminEmptyState
                      icon={<CalendarIcon className="h-6 w-6" />}
                      title="No reservations yet"
                      description="Once reservations begin flowing through the tenant, they will appear here for operator review."
                    />
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {recentReservations.slice(0, 4).map((reservation) => (
                      <div key={reservation.id} className="admin-subpanel rounded-card p-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-on-surface">{reservationGuestLabel(reservation)}</p>
                              <span className={`admin-soft-pill inline-flex rounded-pill px-2 py-1 text-[11px] font-semibold capitalize ${reservationStatusTone(reservation.status)}`}>
                                {reservation.status}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-on-surface-variant">{vehicleLabel(reservation)}</p>
                            <p className="mt-1 text-xs text-on-surface-variant">
                              {formatDate(reservation.start_date, { month: 'short', day: 'numeric' })} to {formatDate(reservation.end_date, { month: 'short', day: 'numeric' })} · created {formatDate(reservation.created_at)}
                            </p>
                          </div>
                          <div className="text-left md:text-right">
                            <p className="text-sm font-semibold text-on-surface">{formatCurrency(reservation.total_price)}</p>
                            <p className="text-xs text-on-surface-variant">Booking value</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ComingSoonOverlay>
          </div>
        </AdminTableCard>
      </div> */}
    </PageLayout>
  );
}

export default AdminDashboard;
